import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Separator } from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { getNodeTestIdProp } from '@/utils';
import { PRODUCT_RANGE_FILTERS, toRangePatch } from '../productListFilters';
import type { CategoryFilterOption } from '../productsMeta';
import type { NamedOption } from './productFiltersMeta';
import type { FilterOptionsState } from './useFilterOptionsState';
import type { ProductFiltersDrawerState } from '../../useProductFiltersDrawer';
import type { ProductBranchScope } from './branchScopeMeta';
import { BranchScopeField } from './BranchScopeField';
import { BrandFilterPicker } from './BrandFilterPicker';
import { CategoryFilterPicker } from './CategoryFilterPicker';
import { RangeFilterField } from './RangeFilterField';
import { StockFilterFields } from './StockFilterFields';
import { TypeFilterToggle } from './TypeFilterToggle';

type ProductFiltersDrawerProps = {
    drawer: ProductFiltersDrawerState;
    categoryOptions: FilterOptionsState<CategoryFilterOption>;
    brandOptions: FilterOptionsState<NamedOption>;
    branchScope: ProductBranchScope;
};

/**
 * Every Products filter but the search (BOS-199), on the shared `FormDrawer`:
 * full screen below `sm`, a scrolling body and a pinned Reset / Cancel /
 * Apply footer. The fields edit the drawer's held copy; nothing reaches the
 * list until Apply, which stays off while a range's minimum is above its
 * maximum (BOS-203). The branch whose stock the figures count heads it
 * (bos-app#118).
 */
export const ProductFiltersDrawer: React.FC<ProductFiltersDrawerProps> = ({
    drawer,
    categoryOptions,
    brandOptions,
    branchScope,
}) => {
    const { t } = useTranslation('pages');
    const { draft, onChange } = drawer;

    return (
        <FormDrawer
            footer={
                <>
                    {/* Reset sits apart from the pair that close the drawer:
                        it only clears the fields. */}
                    <Button
                        className="mr-auto"
                        onPress={drawer.onReset}
                        variant="ghost"
                        {...getNodeTestIdProp('product-filters-reset')}
                    >
                        {t('products.components.productTable.filters.reset')}
                    </Button>
                    <Button
                        onPress={drawer.onClose}
                        variant="secondary"
                        {...getNodeTestIdProp('product-filters-cancel')}
                    >
                        {t('products.components.productTable.filters.cancel')}
                    </Button>
                    <Button
                        isDisabled={!drawer.canApply}
                        onPress={drawer.onApply}
                        variant="primary"
                        {...getNodeTestIdProp('product-filters-apply')}
                    >
                        {t('products.components.productTable.filters.apply')}
                    </Button>
                </>
            }
            isOpen={drawer.isOpen}
            onClose={drawer.onClose}
            subtitle={t(
                'products.components.productTable.filters.drawerSubtitle',
            )}
            testId="product-filters-drawer"
            title={t('products.components.productTable.filters.drawerTitle')}
        >
            <div className="flex flex-col gap-4">
                {/* Whose stock the figures count frames the filters under it
                    rather than being one, so a separator sets it apart. */}
                {branchScope.mode === 'none' ? null : (
                    <>
                        <BranchScopeField
                            copy={{
                                label: t(
                                    'products.components.productTable.filters.branchLabel',
                                ),
                                pickerDescription: t(
                                    'products.components.productTable.filters.branchDescription',
                                ),
                                lineDescription: t(
                                    'products.components.productTable.filters.branchReadOnlyDescription',
                                ),
                            }}
                            layout="stacked"
                            onChange={(branchId) => onChange({ branchId })}
                            scope={branchScope}
                            value={draft.branchId}
                        />
                        <Separator />
                    </>
                )}
                <CategoryFilterPicker
                    onChange={onChange}
                    options={categoryOptions}
                    value={{
                        categoryIds: draft.categoryIds,
                        noCategory: draft.noCategory,
                    }}
                />
                <BrandFilterPicker
                    onChange={onChange}
                    options={brandOptions}
                    value={{ brandIds: draft.brandIds, noBrand: draft.noBrand }}
                />
                <TypeFilterToggle
                    onChange={(type) => onChange({ type })}
                    value={draft.type}
                />
                {PRODUCT_RANGE_FILTERS.map((filter) => (
                    <RangeFilterField
                        filter={filter}
                        isInverted={drawer.invertedRanges.includes(filter)}
                        key={filter}
                        onChange={(range) =>
                            onChange(toRangePatch(filter, range))
                        }
                        value={draft[filter]}
                    />
                ))}
                <StockFilterFields
                    draft={draft.draft}
                    includeArchived={draft.includeArchived}
                    lowStock={draft.lowStock}
                    onChange={onChange}
                />
            </div>
        </FormDrawer>
    );
};
